import { useEffect, useRef, useState } from "react";
import { Alert, Button, Input, Spin } from "antd";
import { ApiError, exchangeCrmSession, loginWithCrm, setToken } from "../api";
import "./login.css";
/**
 * Вход как в Базе знаний: логин и пароль CRM. Session-cookie CRM живёт на
 * .brasol.ru, поэтому если юзер уже залогинен в CRM — входим молча без
 * формы (silent SSO). Логин идёт через прокси /api/crm/login → CRM.
 * Визуал — спека 00 прототипа; логика и тексты ошибок (api.auth.test.ts) без изменений.
 */
export default function LoginScreen({
  error,
  onToken,
}: {
  error: string | null;
  onToken: () => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [manual, setManual] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [busyForm, setBusyForm] = useState(false);
  const [silentTried, setSilentTried] = useState(!!error);
  const silentRequest = useRef<Promise<{ name: string }> | null>(null);
  const submitInFlight = useRef(false);

  // Уже залогинен в CRM (cookie .brasol.ru) → входим сами.
  useEffect(() => {
    if (error) {
      setSilentTried(true);
      return;
    }
    let active = true;
    // Не запускаем второй обмен одноразовой ссылки при повторной подписке StrictMode.
    silentRequest.current ??= exchangeCrmSession();
    silentRequest.current
      .then(() => {
        if (active) onToken();
      })
      .catch((cause: unknown) => {
        if (
          active &&
          !(cause instanceof ApiError && [401, 403, 409].includes(cause.status))
        )
          setFormError(
            cause instanceof Error
              ? cause.message
              : "Не удалось выполнить автоматический вход. Войдите с логином и паролем.",
          );
      })
      .finally(() => {
        if (active) setSilentTried(true);
      });
    return () => {
      active = false;
    };
  }, [error, onToken]);

  const submit = async () => {
    if (submitInFlight.current || !email || !password) return;
    submitInFlight.current = true;
    setBusyForm(true);
    setFormError(null);
    try {
      await loginWithCrm(email, password);
      onToken();
    } catch (cause) {
      setFormError(
        cause instanceof Error
          ? cause.message
          : "Не удалось войти. Попробуйте ещё раз.",
      );
    } finally {
      submitInFlight.current = false;
      setBusyForm(false);
    }
  };

  return (
    <div className="lg-root">
      <img
        className="lg-hero"
        src="/assets/logo/fishka-mark-sky.svg"
        alt=""
        aria-hidden="true"
      />
      <div className="lg-card">
        <div className="lg-logo">
          <img
            className="fishka-login-logo"
            src="/assets/logo/fishka-lockup-sky.svg"
            alt="FISHKA Marketing"
          />
        </div>
        {!silentTried ? (
          <Spin spinning>
            <div className="lg-silent" role="status" aria-live="polite">
              Проверяем сессию CRM…
            </div>
          </Spin>
        ) : (
          <>
            <p className="lg-sub">Войдите с логином и паролем от CRM.</p>
            {error && <Alert type="warning" message={error} />}
            {formError && <Alert type="error" message={formError} />}
            <form
              className="lg-form"
              onSubmit={(event) => {
                event.preventDefault();
                void submit();
              }}
            >
              <Input
                placeholder="E-mail"
                type="email"
                name="username"
                autoComplete="username"
                aria-label="E-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Input.Password
                placeholder="Пароль"
                name="password"
                autoComplete="current-password"
                aria-label="Пароль"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button
                type="primary"
                htmlType="submit"
                block
                disabled={!email || !password}
                loading={busyForm}
              >
                Войти
              </Button>
              {!showToken ? (
                <button
                  type="button"
                  className="lg-link"
                  onClick={() => setShowToken(true)}
                >
                  вход по сервисному токену
                </button>
              ) : (
                <>
                  <Input.Password
                    placeholder="Сервисный токен"
                    aria-label="Сервисный токен"
                    value={manual}
                    onChange={(e) => setManual(e.target.value)}
                  />
                  <Button
                    block
                    disabled={!manual.trim() || busyForm}
                    onClick={() => {
                      setToken(manual.trim());
                      onToken();
                    }}
                  >
                    Войти с токеном
                  </Button>
                </>
              )}
            </form>
          </>
        )}
        <p className="lg-note">
          Уже вошли в CRM? Кабинет откроется автоматически.
          <br />
          Пароль меняется в CRM БРАСОЛ.
        </p>
      </div>
    </div>
  );
}
